import { createTheme } from "@mui/material/styles";

/* ============================================================
   Sistema de diseño — Datos para la Democracia
   Identidad: azul institucional Nodo XXI + acentos FES.
   ============================================================ */

export const BRAND = {
  nodoBlue:   "#0B3D91",
  nodoBlueDk: "#082B73",
  nodoNavy:   "#061D4E",   // fondos profundos (footer, héroes)
  nodoSky:    "#3E6FD9",   // azul claro para gradientes y hovers
  fesRed:     "#D70000",
  fesRedDk:   "#A80404",
  fesYellow:  "#FFCC00",
  fesBlue:    "#005597",
  white:      "#FFFFFF",
  bg:         "#F4F6FB",
  cardBg:     "#FFFFFF",
  border:     "#E2E6EF",
  borderSoft: "#ECEFF6",
  text:       "#151B2B",
  textSub:    "#5A6172",
  textFaint:  "#8C93A6",
};

/* Acento por sección temática — única fuente de verdad del color
   que usan héroes, tarjetas, sidebar y series principales. */
export const SECTION_COLORS = {
  economia:  { main: "#0B3D91", dark: "#082B73", soft: "#E9EFFB" },
  trabajo:   { main: "#006838", dark: "#014D2B", soft: "#E7F4ED" },
  salud:     { main: "#005597", dark: "#003F70", soft: "#E6F1F9" },
  migracion: { main: "#6B21A8", dark: "#521580", soft: "#F2EAFB" },
  seguridad: { main: "#D70000", dark: "#A50505", soft: "#FCEAEA" },
};

export const FONT_BODY =
  '"Inter", "Poppins", "Roboto", "Helvetica", "Arial", sans-serif';
export const FONT_DISPLAY =
  '"Poppins", "Inter", "Roboto", "Helvetica", "Arial", sans-serif';

/* Sombras suaves en frío (tinte azul) — tres niveles */
export const SHADOWS = {
  sm: "0 1px 2px rgba(9, 20, 50, 0.05), 0 1px 8px rgba(9, 20, 50, 0.04)",
  md: "0 10px 28px -10px rgba(9, 20, 50, 0.16)",
  lg: "0 24px 56px -20px rgba(9, 20, 50, 0.28)",
};

/* Anillo de foco accesible reutilizable */
const FOCUS_RING = `0 0 0 3px ${BRAND.bg}, 0 0 0 6px rgba(11, 61, 145, 0.45)`;

const theme = createTheme({
  palette: {
    primary:    { main: BRAND.nodoBlue, dark: BRAND.nodoBlueDk, contrastText: "#fff" },
    secondary:  { main: BRAND.fesRed, dark: BRAND.fesRedDk, contrastText: "#fff" },
    background: { default: BRAND.bg, paper: BRAND.cardBg },
    text:       { primary: BRAND.text, secondary: BRAND.textSub },
    divider:    BRAND.border,
  },

  typography: {
    fontFamily: FONT_BODY,
    h1: { fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: "clamp(2.3rem, 5vw, 3.4rem)", lineHeight: 1.08, letterSpacing: "-0.02em" },
    h2: { fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: "clamp(1.9rem, 3.8vw, 2.6rem)", lineHeight: 1.12, letterSpacing: "-0.015em" },
    h3: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: "clamp(1.6rem, 3vw, 2.15rem)", lineHeight: 1.15, letterSpacing: "-0.01em" },
    h4: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: "1.5rem", lineHeight: 1.2 },
    h5: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: "1.22rem", lineHeight: 1.3 },
    h6: { fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: "1.05rem", lineHeight: 1.35 },
    subtitle1: { fontWeight: 600, lineHeight: 1.5 },
    subtitle2: { fontWeight: 600, lineHeight: 1.5 },
    body1: { lineHeight: 1.7 },
    body2: { lineHeight: 1.65 },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: 0.1 },
    caption: { lineHeight: 1.55 },
    overline: { fontWeight: 700, letterSpacing: "0.12em", lineHeight: 1.6 },
  },

  shape: { borderRadius: 12 },

  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
          paddingInline: 18,
          "&.Mui-focusVisible": { boxShadow: FOCUS_RING },
        },
        sizeLarge: { paddingBlock: 11, paddingInline: 26, fontSize: "0.95rem" },
        containedPrimary: {
          background: BRAND.nodoBlue,
          "&:hover": { background: BRAND.nodoBlueDk },
        },
        containedSecondary: {
          background: BRAND.fesRed,
          "&:hover": { background: BRAND.fesRedDk },
        },
        outlined: { borderWidth: 1.5, "&:hover": { borderWidth: 1.5 } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { "&.Mui-focusVisible": { boxShadow: FOCUS_RING } },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${BRAND.border}`,
          boxShadow: SHADOWS.sm,
          backgroundImage: "none",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 14 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: "0.75rem", borderRadius: 8 },
        sizeSmall: { fontSize: "0.72rem" },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: BRAND.nodoNavy,
          borderRadius: 8,
          fontSize: "0.72rem",
          fontWeight: 500,
          paddingInline: 10,
          paddingBlock: 6,
        },
        arrow: { color: BRAND.nodoNavy },
      },
      defaultProps: { arrow: true },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, fontSize: "0.85rem", lineHeight: 1.6 },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 8 },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#fff",
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BRAND.nodoSky },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderWidth: 2 },
        },
        notchedOutline: { borderColor: BRAND.border },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: { backgroundColor: BRAND.borderSoft },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: BRAND.borderSoft },
      },
    },
  },
});

export default theme;
